import React, { useState } from "react";
import { Typography } from "@material-tailwind/react";
import { PlayIcon, PauseIcon, MusicalNoteIcon, EyeIcon, ArrowsRightLeftIcon } from "@heroicons/react/24/solid";
import { SparklesIcon } from "@heroicons/react/24/outline";
import SharePopover from "./SharePopover";
import FavouriteBtn from "./FavouriteBtn";
import { DetailPageUrl } from "../services/API";
import { getYear, returnSubCategoryName } from "../helper/helper";
import PlaylistItem from "./PlaylistItem";

export default function PlaylistComponent({
  rating = "4.0",
  views = 0,
  type = "playlist",
  description,
  image,
  name,
  title,
  author,
  artist,
  sub_category = [],
  released_date,
  publish_date,
  duration_time = "",
  onClickBuy,
  isPurchased = false,
  onWishClick,
  onPlayClick,
  onFeaturedClick,
  feature_video,
  is_favourite,
  id,
  products = [],
  tracks = [],
  currentTrack = null,
  isPlaying = false,
  onTrackSelect,
  onPlayAll,
  onShuffle,
  dark = true,
}) {
  const [imgError, setImgError] = useState(false);
  const playlistTitle = name || title || "Curated Playlist";
  const playlistAuthor = artist || author || "BAB Media Official";
  const playlistSongs = Array.isArray(products) && products.length > 0
    ? products
    : Array.isArray(tracks) && tracks.length > 0
    ? tracks
    : [];

  const subCatText = returnSubCategoryName(sub_category);

  return (
    <div className="px-4 sm:px-6 lg:px-14 py-6 sm:py-8">
      {/* Hero Showcase Banner */}
      <section
        id="PlaylistHero"
        className={`relative overflow-hidden rounded-3xl border ${
          dark
            ? "bg-zinc-900/60 border-zinc-800/80 backdrop-blur-xl shadow-2xl shadow-black/60"
            : "bg-white border-gray-200 shadow-xl"
        } p-6 sm:p-8 lg:p-10 mb-8`}
      >
        {/* Subtle Ambient Background Glow */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20"></div>

        <div className="relative z-10 flex flex-col-reverse lg:flex-row gap-8 lg:gap-12 items-center lg:items-stretch justify-between">
          {/* Metadata & Actions */}
          <div className="flex flex-col justify-center flex-1 w-full text-center lg:text-left">
            <div className="flex items-center justify-center lg:justify-start gap-2 mb-3">
              <span className="px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-secondary/20 text-secondary border border-secondary/30 flex items-center gap-1.5 shadow-sm">
                <SparklesIcon className="w-3.5 h-3.5" />
                Curated Playlist
              </span>
              <span className="text-zinc-500 text-xs font-semibold">•</span>
              <span className="text-zinc-400 text-xs font-medium">SoulFlow Records</span>
            </div>

            <h1
              className={`text-2xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-3 leading-tight ${
                dark ? "text-white" : "text-black"
              }`}
            >
              {playlistTitle}
            </h1>

            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-4 mb-4 text-xs sm:text-sm font-medium">
              <span className="flex items-center gap-1.5 text-secondary font-bold">
                <MusicalNoteIcon className="h-4 w-4" />
                {subCatText || "Soulful Music"}
              </span>
              <span className={`h-1 w-1 rounded-full ${dark ? "bg-zinc-700" : "bg-gray-300"}`}></span>
              <span className="flex items-center gap-1.5 text-zinc-400">
                <EyeIcon className="h-4 w-4" />
                {views || 1} views
              </span>
              {(publish_date || released_date) && (
                <>
                  <span className={`h-1 w-1 rounded-full ${dark ? "bg-zinc-700" : "bg-gray-300"}`}></span>
                  <span className="text-zinc-400 font-medium">
                    {getYear(publish_date || released_date)}
                  </span>
                </>
              )}
              <span className={`h-1 w-1 rounded-full ${dark ? "bg-zinc-700" : "bg-gray-300"}`}></span>
              <span className="text-zinc-300 font-semibold">
                {playlistSongs.length} Tracks
              </span>
            </div>

            {description && (
              <p
                className={`mb-6 text-xs sm:text-sm sm:leading-relaxed ${
                  dark ? "text-zinc-400" : "text-gray-600"
                } max-w-2xl line-clamp-3`}
              >
                {description}
              </p>
            )}

            <Typography
              className={`text-xs sm:text-sm mb-6 font-medium ${
                dark ? "text-zinc-400" : "text-gray-500"
              }`}
            >
              Curated by <span className="font-bold text-secondary">{playlistAuthor}</span> •{" "}
              {playlistSongs.length} Tracks available
            </Typography>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-4">
              <button
                onClick={onPlayAll}
                disabled={playlistSongs.length === 0}
                className="px-7 py-3 rounded-xl font-bold text-sm bg-secondary hover:bg-secondary/90 text-white shadow-lg shadow-secondary/30 transition-all duration-300 hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isPlaying ? (
                  <>
                    <PauseIcon className="h-4 w-4 fill-white" />
                    <span>Pause Playlist</span>
                  </>
                ) : currentTrack ? (
                  <>
                    <PlayIcon className="h-4 w-4 fill-white" />
                    <span>Resume Playlist</span>
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-4 w-4 fill-white" />
                    <span>Play All Tracks</span>
                  </>
                )}
              </button>

              {onShuffle && playlistSongs.length > 1 && (
                <button
                  onClick={onShuffle}
                  className="px-5 py-3 rounded-xl font-bold text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700/80 transition-all duration-300 hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2"
                >
                  <ArrowsRightLeftIcon className="h-4 w-4" />
                  Shuffle
                </button>
              )}

              {feature_video && onFeaturedClick && (
                <button
                  onClick={onFeaturedClick}
                  className="px-5 py-3 rounded-xl font-bold text-sm border border-zinc-700 hover:border-secondary bg-white/5 hover:bg-white/10 text-white transition-all duration-300 hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2"
                >
                  Watch Promo
                </button>
              )}

              <div className="flex items-center gap-3">
                {onWishClick && (
                  <FavouriteBtn is_favourite={is_favourite} onClick={onWishClick} dark={dark} />
                )}
                <SharePopover link={`${DetailPageUrl}?id=${id}&type=playlist`} dark={dark} />
              </div>
            </div>
          </div>

          {/* Interactive Artwork & Vinyl Disc Animation */}
          <div className="relative flex items-center justify-center w-52 h-52 sm:w-64 sm:h-64 lg:w-72 lg:h-72 flex-shrink-0">
            {/* Spinning Vinyl Disc behind the album art */}
            <div
              className={`absolute w-[86%] h-[86%] flex items-center justify-center transition-all duration-700 ${
                isPlaying ? "translate-x-12 sm:translate-x-16" : "translate-x-4 sm:translate-x-6"
              }`}
            >
              <div
                className={`w-full h-full rounded-full shadow-2xl shadow-black/80 flex items-center justify-center ${
                  isPlaying ? "animate-spin-slow" : ""
                }`}
                style={{
                  backgroundImage:
                    "repeating-radial-gradient(circle, #27272a 0px, #27272a 2px, #09090b 3px, #09090b 6px)",
                }}
              >
                <div className="w-16 h-16 rounded-full bg-secondary/90 border-2 border-black flex items-center justify-center shadow-inner">
                  <div className="w-4 h-4 rounded-full bg-black"></div>
                </div>
              </div>
            </div>

            {/* Front Cover Artwork */}
            <div className="relative z-10 w-[84%] h-[84%] rounded-2xl overflow-hidden shadow-2xl shadow-black/90 border border-zinc-700/50 bg-zinc-950 transform group-hover:scale-102 transition-transform duration-500">
              {image && !imgError ? (
                <img
                  className="w-full h-full object-cover"
                  src={image}
                  alt={playlistTitle}
                  onError={() => setImgError(true)}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-orange-600 via-zinc-900 to-black p-5 flex flex-col justify-between">
                  <span className="text-[10px] font-black uppercase text-secondary tracking-widest">
                    BAB Media
                  </span>
                  <div className="flex flex-col items-center justify-center">
                    <MusicalNoteIcon className="w-10 h-10 text-secondary mb-1 drop-shadow" />
                    <p className="text-white font-bold text-xs text-center line-clamp-2">
                      {playlistTitle}
                    </p>
                  </div>
                  <span className="text-[10px] text-zinc-400 font-semibold text-right">
                    {playlistSongs.length} Tracks
                  </span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* Tracks List Section */}
      <div
        className={`rounded-2xl border ${
          dark ? "bg-zinc-900/40 border-zinc-800/80 backdrop-blur-md" : "bg-white border-gray-200"
        } overflow-hidden shadow-xl`}
      >
        <div className="px-5 sm:px-6 py-4 sm:py-5 border-b border-zinc-800/80 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-secondary"></span>
            <Typography className={`text-base sm:text-lg font-bold ${dark ? "text-white" : "text-black"}`}>
              Playlist Tracks
            </Typography>
          </div>
          <span className="text-xs font-black text-secondary bg-secondary/15 px-3 py-1 rounded-full border border-secondary/25">
            {playlistSongs.length} TRACKS
          </span>
        </div>

        {playlistSongs.length === 0 ? (
          <div className="py-16 text-center px-4">
            <MusicalNoteIcon className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-base font-bold text-zinc-300 mb-1">
              Tracks Being Added
            </p>
            <p className="text-xs text-zinc-500 max-w-sm mx-auto">
              Our curators are actively updating this playlist with fresh tracks. Check back shortly!
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full leading-normal">
              <thead>
                <tr className={dark ? "bg-zinc-950/60" : "bg-gray-50"}>
                  <th className="px-3 sm:px-4 py-3.5 border-b border-zinc-800 text-center text-xs font-bold uppercase tracking-wider text-zinc-400 w-10 sm:w-14">
                    #
                  </th>
                  <th className="px-3 sm:px-4 py-3.5 border-b border-zinc-800 text-left text-xs font-bold uppercase tracking-wider text-zinc-400">
                    Song & Artist
                  </th>
                  <th className="px-3 sm:px-4 py-3.5 border-b border-zinc-800 text-center text-xs font-bold uppercase tracking-wider text-zinc-400">
                    Access
                  </th>
                  <th className="hidden md:table-cell px-4 py-3.5 border-b border-zinc-800 text-center text-xs font-bold uppercase tracking-wider text-zinc-400">
                    Released
                  </th>
                  <th className="px-3 sm:px-4 py-3.5 border-b border-zinc-800 text-right sm:text-center text-xs font-bold uppercase tracking-wider text-zinc-400">
                    Duration
                  </th>
                </tr>
              </thead>
              <tbody>
                {playlistSongs.map((track, i) => {
                  const isCurrentActive = currentTrack?.id === track?.id;
                  return (
                    <PlaylistItem
                      key={track?.id || i}
                      index={i}
                      {...track}
                      isActive={isCurrentActive}
                      isPlaying={isCurrentActive && isPlaying}
                      onTrackClick={() => onTrackSelect && onTrackSelect(track, i)}
                      dark={dark}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
